import { openInPanel } from "./p9-workspace.js";

type Tag = { id: string; label: string };
type PageCard = { id: string; title: string; url: string; excerpt: string; tags: Tag[] };
type BoardNode = {
  id: string;
  kind: "note" | "page";
  title: string;
  body: string;
  bodyHtml: string;
  pageId: string | null;
  page: PageCard | null;
  x: number;
  y: number;
  width: number;
  height: number;
  shape: string;
  color: string;
  border: string;
  restrictedTo: string;
  noAi: boolean;
  tags: Tag[];
  gone?: boolean;
};
type Relation = { id: string; from: string; to: string; label: string | null };
type Sizes = { heading: number | null; body: number | null; label: number | null };
type Board = {
  id: string;
  title: string;
  pageId: string;
  canEdit: boolean;
  nodes: BoardNode[];
  relations: Relation[];
  shapes: string[];
  colors: string[];
  borders: string[];
  textSizes: Sizes;
};

// Default text sizes in pixels, when the person hasn't chosen their own.
const SIZE_DEFAULTS = { heading: 15, body: 14, label: 12 } as const;
const SIZE_LABELS: Record<keyof Sizes, string> = { heading: "Headings", body: "Node text", label: "Relation labels" };

const SHAPE_LABELS: Record<string, string> = { card: "Card", note: "Sticky note", round: "Round", cloud: "Thought cloud", shout: "Shout", speech: "Speech bubble" };
const SVG_SHAPES = new Set(["cloud", "shout", "speech"]);
const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * A board (docs/boards-design.md): nodes and labelled relations on a canvas you pan and zoom.
 *
 * View mode (on pages): look around, highlight by tag, list, full screen, side panel; clicking a node shows it.
 * Edit mode (in the page's edit view, or switched on in the side panel): add notes and page nodes, move, resize,
 * edit, style, tag, restrict and relate them; every change is saved at once, per node or relation.
 *
 * Attributes: data-url (the board's endpoint), data-base (the archive's root), data-mode ("edit" or "view"),
 * data-toggle-edit (view mode may switch to editing in place), data-orphan (no longer in the page's text),
 * data-generated (a board worked out by the server, like the Weave: never stored, so no side panel), data-board-json
 * (the board itself, for the first drawing; reloading asks data-url).
 */
export class P9Board extends HTMLElement {
  #url = "";
  #base = "";
  #board: Board | null = null;
  #editing = false;
  #viewport!: HTMLElement;
  #world!: HTMLElement;
  #lines!: SVGSVGElement;
  #list!: HTMLElement;
  #panel!: HTMLFormElement;
  #status!: HTMLElement;
  #tagBar!: HTMLElement;
  #title!: HTMLElement;
  #editTools!: HTMLElement;
  #modeTools!: HTMLElement;
  #relateButton!: HTMLButtonElement;
  #view = { x: 40, y: 40, scale: 1 };
  #filter: string | null = null;
  #selected: string | null = null;
  #relateFrom: string | null = null;
  #relating = false;
  #pendingLabel: string | null = null;
  #pinnedNote!: HTMLElement;
  #sizes: Sizes = { heading: null, body: null, label: null };
  #sizesPopover: HTMLElement | null = null;
  #sizesTimer: number | undefined;
  #showAnyway = false;
  #inline: string | null = null;

  connectedCallback(): void {
    this.#url = this.dataset.url ?? "";
    this.#base = this.dataset.base ?? document.body.dataset.base ?? "/";
    this.#inline = this.dataset.boardJson ?? null;
    delete this.dataset.boardJson;
    this.classList.add("board");
    this.replaceChildren();
    this.#build();
    void this.#load();
    // Text sizes are the person's own, the same on every board: one board changing them updates the others here.
    document.addEventListener("p9-board-sizes", (event) => this.#applySizes((event as CustomEvent<Sizes>).detail));
    // In the workspace's main area: fold away while this same board is pinned in the side panel.
    if (window !== window.top && window.name === "main" && this.dataset.generated !== "true") {
      window.addEventListener("message", (event) => {
        if (event.origin === window.location.origin && typeof (event.data as { p9Panel?: unknown })?.p9Panel === "string") this.#checkPinned();
      });
      this.#checkPinned();
    }
  }

  #checkPinned(): void {
    let panelPath = "";
    try {
      panelPath = (window.parent as unknown as Record<string, Window | undefined>)["panel"]?.location.pathname ?? "";
    } catch {
      /* not ours to read */
    }
    const pinned = panelPath.endsWith(`/boards/${this.#boardId()}/open`);
    if (!pinned) this.#showAnyway = false;
    this.classList.toggle("is-pinned", pinned && !this.#showAnyway);
    this.#pinnedNote.hidden = !pinned || this.#showAnyway;
  }

  // ---- Data ----

  async #load(): Promise<void> {
    try {
      if (this.#inline) {
        this.#board = JSON.parse(this.#inline) as Board;
        this.#inline = null;
      } else {
        const response = await fetch(this.#url, { headers: { Accept: "application/json" } });
        if (!response.ok) {
          this.#say(response.status === 404 ? "This board is gone, or you can't see it." : "Couldn't load the board.");
          return;
        }
        this.#board = (await response.json()) as Board;
      }
    } catch {
      this.#say("Couldn't load the board.");
      return;
    }
    this.#title.textContent = this.#board.title;
    // A generated board sits in a box that names it already.
    this.#title.hidden = this.dataset.generated === "true";
    this.#applySizes(this.#board.textSizes ?? this.#sizes);
    this.#setEditing(this.dataset.mode === "edit" && this.#board.canEdit);
    this.#fit();
  }

  async #call<T>(handler: string, query: Record<string, string>, body: unknown): Promise<T | null> {
    const params = new URLSearchParams({ handler, ...query });
    try {
      const response = await fetch(`${this.#url}?${params}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", RequestVerificationToken: token() },
        body: JSON.stringify(body ?? {}),
      });
      const result = (await response.json().catch(() => ({}))) as T & { error?: string };
      if (!response.ok) {
        this.#say(result.error ?? "That didn't work.");
        return null;
      }
      this.#say("Saved");
      return result;
    } catch {
      this.#say("Couldn't save; check your connection.");
      return null;
    }
  }

  #node(id: string): BoardNode | undefined {
    return this.#board?.nodes.find((n) => n.id === id);
  }

  #replace(node: BoardNode): void {
    if (!this.#board) return;
    const others = this.#board.nodes.filter((n) => n.id !== node.id);
    this.#board.nodes = node.gone ? others : [...others, node];
    if (node.gone) this.#board.relations = this.#board.relations.filter((r) => r.from !== node.id && r.to !== node.id);
    this.#render();
  }

  // ---- Building ----

  #build(): void {
    const bar = div("board-toolbar");
    this.#title = document.createElement("strong");
    this.#title.className = "board-title";
    this.#editTools = div("board-tools");
    this.#editTools.hidden = true;
    this.#relateButton = button("Relate", "Connect two nodes: click the node it starts from, then the one it points to", () => this.#toggleRelate());
    const deleteBoard = button("Delete board", "This board is no longer in the page's text; delete it for good", () => void this.#deleteBoard());
    deleteBoard.className = "danger";
    deleteBoard.hidden = this.dataset.orphan !== "true";
    this.#editTools.append(
      button("+ Note", "Add a note", () => void this.#addNote()),
      button("+ Page", "Add a node that stands for a wiki page", () => this.#openEditor(null, "page")),
      this.#relateButton,
      deleteBoard,
    );
    this.#modeTools = div("board-tools");
    const viewTools = div("board-tools");
    viewTools.append(
      button("−", "Zoom out", () => this.#zoomBy(1 / 1.2)),
      button("+", "Zoom in", () => this.#zoomBy(1.2)),
      button("Fit", "Show the whole board", () => this.#fit()),
      button("List", "Show the nodes as a list", () => this.#toggleList()),
      button("↻", "Reload (see others' changes)", () => void this.#load()),
      button("⛶", "Full screen", () => this.#toggleFull()),
      button("Aa", "Text sizes on boards (yours, on every board)", () => this.#toggleSizes(bar)),
    );
    if (window.name !== "panel" && this.dataset.generated !== "true")
      viewTools.append(button("⇥ Side panel", "Pin this board next to the wiki while you browse", () => openInPanel(`${this.#base}boards/${this.#boardId()}/open`)));
    bar.append(this.#title, this.#modeTools, this.#editTools, viewTools);

    this.#tagBar = div("board-tags");
    this.#viewport = div("board-viewport");
    this.#viewport.tabIndex = 0;
    this.#viewport.setAttribute("aria-label", "Board canvas: drag to pan, Ctrl + scroll to zoom");
    this.#world = div("board-world");
    this.#lines = document.createElementNS(SVG_NS, "svg");
    this.#lines.classList.add("board-lines");
    this.#lines.innerHTML =
      '<defs><marker id="board-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" /></marker></defs><g class="board-paths"></g>';
    this.#world.append(this.#lines);
    this.#viewport.append(this.#world);
    this.#list = div("board-list");
    this.#list.hidden = true;
    this.#panel = document.createElement("form");
    this.#panel.className = "board-panel";
    this.#panel.hidden = true;
    this.#status = document.createElement("p");
    this.#status.className = "board-status muted small";
    this.#status.setAttribute("aria-live", "polite");
    // Node details and the node editor float on the canvas, next to their node.
    const stage = div("board-stage");
    stage.append(this.#viewport, this.#panel);
    this.#pinnedNote = div("board-pinned muted small");
    this.#pinnedNote.hidden = true;
    this.#pinnedNote.append(
      "This board is open in the side panel. ",
      button("Show here too", "Show the board on the page as well", () => {
        this.#showAnyway = true;
        this.#checkPinned();
        requestAnimationFrame(() => this.#fit());
      }),
    );
    this.append(bar, this.#pinnedNote, this.#tagBar, stage, this.#list, this.#status);
    this.#wireCanvas();
  }

  #applySizes(sizes: Sizes): void {
    this.#sizes = { ...sizes };
    for (const key of ["heading", "body", "label"] as const) {
      const px = sizes[key];
      if (px === null) this.style.removeProperty(`--board-${key}`);
      else this.style.setProperty(`--board-${key}`, `${px}px`);
    }
    if (this.#board) {
      this.#drawLines();
      this.#renderSizes();
    }
  }

  #toggleSizes(bar: HTMLElement): void {
    if (this.#sizesPopover) {
      this.#sizesPopover.remove();
      this.#sizesPopover = null;
      return;
    }
    this.#sizesPopover = div("board-sizes");
    bar.append(this.#sizesPopover);
    this.#renderSizes();
  }

  #renderSizes(): void {
    const pop = this.#sizesPopover;
    if (!pop) return;
    pop.replaceChildren();
    const intro = document.createElement("strong");
    intro.className = "small";
    intro.textContent = "Text sizes (yours, on every board)";
    pop.append(intro);
    for (const key of ["heading", "body", "label"] as const) {
      const row = div("row");
      const name = document.createElement("span");
      name.textContent = SIZE_LABELS[key];
      const value = document.createElement("span");
      value.className = "value";
      const current = this.#sizes[key] ?? SIZE_DEFAULTS[key];
      value.textContent = `${current}px`;
      const change = (delta: number) => this.#setSizes({ ...this.#sizes, [key]: Math.min(40, Math.max(8, current + delta)) });
      row.append(name, button("−", `Smaller ${SIZE_LABELS[key].toLowerCase()}`, () => change(-1)), value, button("+", `Larger ${SIZE_LABELS[key].toLowerCase()}`, () => change(1)));
      pop.append(row);
    }
    const reset = button("Reset", "Back to the standard sizes", () => this.#setSizes({ heading: null, body: null, label: null }));
    reset.className = "link";
    pop.append(reset);
  }

  // Applies everywhere on this page at once, and saves to the person's account a moment later.
  #setSizes(sizes: Sizes): void {
    document.dispatchEvent(new CustomEvent<Sizes>("p9-board-sizes", { detail: sizes }));
    window.clearTimeout(this.#sizesTimer);
    this.#sizesTimer = window.setTimeout(async () => {
      try {
        const response = await fetch(`${this.#base}boards?handler=TextSizes`, {
          method: "POST",
          headers: { "Content-Type": "application/json", RequestVerificationToken: token() },
          body: JSON.stringify(sizes),
        });
        this.#say(response.ok ? "Text sizes saved for your account." : "Couldn't save the text sizes.");
      } catch {
        this.#say("Couldn't save the text sizes.");
      }
    }, 500);
  }

  #boardId(): string {
    return this.#board?.id ?? this.#url.split("/").pop() ?? "";
  }

  // Edit mode on or off: the edit tools, and in view mode a way into editing for those who may.
  #setEditing(on: boolean): void {
    this.#editing = on;
    this.classList.toggle("is-editing", on);
    this.#editTools.hidden = !on;
    this.#modeTools.replaceChildren();
    const board = this.#board;
    if (board?.canEdit) {
      if (on && this.dataset.toggleEdit === "true") this.#modeTools.append(button("Done", "Back to viewing", () => this.#setEditing(false)));
      if (!on) {
        const edit = button("✎ Edit board", "Change this board", () => {
          if (this.dataset.toggleEdit === "true") this.#setEditing(true);
          else window.location.href = `${this.#base}p/${board.pageId}/-/edit#board-${board.id}`;
        });
        this.#modeTools.append(edit);
      }
    }
    this.#closePanel();
    this.#relating = false;
    this.#relateFrom = null;
    this.#relateButton.setAttribute("aria-pressed", "false");
    this.#viewport.setAttribute("aria-label", on ? "Board (editing): click a node to edit it, drag to move" : "Board: drag to pan, Ctrl + scroll to zoom, click a node to read it");
    this.#render();
  }

  // ---- Rendering ----

  #render(): void {
    if (!this.#board) return;
    for (const el of this.#world.querySelectorAll(".board-node, .board-relation-label")) el.remove();
    for (const node of this.#board.nodes) this.#world.append(this.#renderNode(node));
    this.#drawLines();
    this.#renderTags();
    if (!this.#list.hidden) this.#renderList();
    this.#placePanel();
  }

  #renderNode(n: BoardNode): HTMLElement {
    const el = div(`board-node shape-${n.shape} color-${n.color} border-${n.border}`);
    el.dataset.id = n.id;
    el.tabIndex = 0;
    el.classList.toggle("dimmed", this.#dimmed(n));
    el.classList.toggle("selected", this.#selected === n.id || this.#relateFrom === n.id);
    Object.assign(el.style, { left: `${n.x}px`, top: `${n.y}px`, width: `${n.width}px`, height: `${n.height}px` });
    if (SVG_SHAPES.has(n.shape)) el.append(shapeSvg(n.shape));

    const content = div("board-node-content");
    if (this.#editing && n.restrictedTo) content.append(label(`Visible to: ${n.restrictedTo}`, "board-restricted"));
    if (this.#editing && n.noAi) content.append(label("No AI", "board-restricted"));
    content.append(...this.#nodeContent(n, true));
    el.append(content);
    if (this.#editing) el.append(div("board-resize"));
    return el;
  }

  // A node's heading, text, page card and tags; shared by the canvas and the read-only details.
  #nodeContent(n: BoardNode, onCanvas: boolean): HTMLElement[] {
    const parts: HTMLElement[] = [];
    if (n.kind === "page") {
      if (n.page) {
        const h = document.createElement("h3");
        const a = document.createElement("a");
        a.href = n.page.url;
        a.textContent = n.title || n.page.title;
        h.append(a);
        const excerpt = document.createElement("p");
        excerpt.className = "board-excerpt";
        excerpt.textContent = n.page.excerpt;
        parts.push(h, excerpt);
        if (n.page.tags.length) parts.push(chips(n.page.tags, "board-page-tags"));
      } else {
        parts.push(label("🔒 A page you can't see", "board-locked"));
      }
    } else if (n.title) {
      const h = document.createElement("h3");
      h.textContent = n.title;
      parts.push(h);
    }
    if (n.bodyHtml) {
      const body = div(onCanvas ? "board-body prose" : "prose");
      // Rendered and sanitised by the server, like comments.
      body.innerHTML = n.bodyHtml;
      parts.push(body);
    }
    if (n.tags.length) parts.push(chips(n.tags, "board-node-tags"));
    return parts;
  }

  #drawLines(): void {
    const board = this.#board!;
    const group = this.#lines.querySelector("g.board-paths")!;
    group.replaceChildren();
    for (const el of this.#world.querySelectorAll(".board-relation-label")) el.remove();

    // Lines per pair of nodes. A relation each way with the same label is one two-way arrow; other relations between
    // the same two nodes bow apart, so every line and label stays readable.
    const pairs = new Map<string, Relation[]>();
    for (const r of board.relations) {
      const key = [r.from, r.to].sort().join("|");
      pairs.set(key, [...(pairs.get(key) ?? []), r]);
    }
    for (const relations of pairs.values()) {
      const [one, other] = relations;
      const twoWay = !!one && !!other && relations.length === 2 && one.from === other.to && one.to === other.from && sameLabel(one.label, other.label);
      const lines: Relation[][] = twoWay ? [relations] : relations.map((r) => [r]);
      lines.forEach((members, i) => this.#drawLine(group, members, (i - (lines.length - 1) / 2) * 56));
    }
  }

  // One line (one relation, or a two-way pair drawn as one), bowed sideways by offset.
  #drawLine(group: Element, members: Relation[], offset: number): void {
    const r = members[0];
    if (!r) return;
    const a = this.#node(r.from);
    const b = this.#node(r.to);
    if (!a || !b) return;
    // Sideways is measured along the pair's own direction (first node to second, by id), so a relation each way
    // with different labels ends up on opposite sides.
    const [first, second] = r.from < r.to ? [a, b] : [b, a];
    const [fx, fy] = [first.x + first.width / 2, first.y + first.height / 2];
    const [sx, sy] = [second.x + second.width / 2, second.y + second.height / 2];
    const length = Math.hypot(sx - fx, sy - fy) || 1;
    const [nx, ny] = [-(sy - fy) / length, (sx - fx) / length];
    // A quadratic curve's middle lies halfway to its control point, so the control point is twice the offset out.
    const [cx, cy] = [(fx + sx) / 2 + nx * offset * 2, (fy + sy) / 2 + ny * offset * 2];
    const [x1, y1] = edgeToward(a, cx, cy);
    const [x2, y2] = edgeToward(b, cx, cy);
    const dim = this.#dimmed(a) || this.#dimmed(b);
    const line = document.createElementNS(SVG_NS, "path");
    line.setAttribute("d", `M${x1},${y1} Q${cx},${cy} ${x2},${y2}`);
    line.setAttribute("marker-end", "url(#board-arrow)");
    if (members.length > 1) line.setAttribute("marker-start", "url(#board-arrow)");
    line.classList.add("board-rel");
    line.classList.toggle("dimmed", dim);
    group.append(line);
    if (!r.label && !this.#editing) return;
    const tag = div("board-relation-label");
    tag.dataset.relations = members.map((m) => m.id).join(" ");
    tag.textContent = r.label ?? "";
    tag.classList.toggle("empty", !r.label);
    tag.classList.toggle("dimmed", dim);
    // The label sits halfway along the curve.
    tag.style.left = `${(x1 + 2 * cx + x2) / 4}px`;
    tag.style.top = `${(y1 + 2 * cy + y2) / 4}px`;
    if (this.#editing) tag.title = members.length > 1 ? "Click to change or remove this two-way relation" : "Click to change or remove this relation";
    this.#world.append(tag);
  }

  #renderTags(): void {
    const all = new Map<string, Tag>();
    for (const n of this.#board?.nodes ?? []) for (const t of [...n.tags, ...(n.page?.tags ?? [])]) all.set(t.id, t);
    this.#tagBar.replaceChildren();
    this.#tagBar.hidden = all.size === 0;
    if (all.size === 0) return;
    const intro = document.createElement("span");
    intro.className = "muted small";
    intro.textContent = "Highlight:";
    this.#tagBar.append(intro);
    for (const t of [...all.values()].sort((x, y) => x.label.localeCompare(y.label))) {
      const b = button(t.label, `Highlight nodes tagged ${t.label}`, () => {
        this.#filter = this.#filter === t.id ? null : t.id;
        this.#render();
      });
      b.className = "tag-chip";
      b.setAttribute("aria-pressed", String(this.#filter === t.id));
      this.#tagBar.append(b);
    }
  }

  #renderList(): void {
    const board = this.#board!;
    this.#list.replaceChildren();
    const ul = document.createElement("ul");
    for (const n of [...board.nodes].sort((a, b) => a.y - b.y || a.x - b.x)) {
      if (this.#dimmed(n)) continue;
      const li = document.createElement("li");
      li.append(...this.#nodeContent(n, false));
      const out = this.#relationLines(n);
      if (out.length) {
        const rel = document.createElement("p");
        rel.className = "muted small";
        rel.textContent = out.join(" · ");
        li.append(rel);
      }
      ul.append(li);
    }
    this.#list.append(ul);
  }

  #relationLines(n: BoardNode): string[] {
    const relations = this.#board?.relations ?? [];
    return [
      ...relations.filter((r) => r.from === n.id).map((r) => `${r.label ? r.label + " → " : "→ "}${this.#name(r.to)}`),
      ...relations.filter((r) => r.to === n.id).map((r) => `${this.#name(r.from)}${r.label ? " → " + r.label : ""} → this`),
    ];
  }

  #name(id: string): string {
    const n = this.#node(id);
    return n ? n.title || n.page?.title || "(untitled)" : "?";
  }

  #dimmed(n: BoardNode): boolean {
    const f = this.#filter;
    return f !== null && !n.tags.some((t) => t.id === f) && !(n.page?.tags.some((t) => t.id === f) ?? false);
  }

  // ---- View ----

  #apply(): void {
    const { x, y, scale } = this.#view;
    this.#world.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    this.#placePanel();
  }

  #zoomBy(factor: number, cx?: number, cy?: number): void {
    const rect = this.#viewport.getBoundingClientRect();
    const px = cx ?? rect.width / 2;
    const py = cy ?? rect.height / 2;
    const scale = Math.min(3, Math.max(0.15, this.#view.scale * factor));
    const k = scale / this.#view.scale;
    this.#view = { x: px - (px - this.#view.x) * k, y: py - (py - this.#view.y) * k, scale };
    this.#apply();
  }

  #fit(): void {
    const nodes = this.#board?.nodes ?? [];
    const rect = this.#viewport.getBoundingClientRect();
    if (nodes.length === 0 || rect.width === 0) {
      this.#view = { x: 40, y: 40, scale: 1 };
      return this.#apply();
    }
    const minX = Math.min(...nodes.map((n) => n.x));
    const minY = Math.min(...nodes.map((n) => n.y));
    const maxX = Math.max(...nodes.map((n) => n.x + n.width));
    const maxY = Math.max(...nodes.map((n) => n.y + n.height));
    const scale = Math.min(1.25, Math.max(0.15, Math.min((rect.width - 60) / (maxX - minX), (rect.height - 60) / (maxY - minY))));
    this.#view = { scale, x: (rect.width - (maxX - minX) * scale) / 2 - minX * scale, y: (rect.height - (maxY - minY) * scale) / 2 - minY * scale };
    this.#apply();
  }

  // Glides the canvas so the node sits in the middle (view mode's answer to a click).
  #centreOn(node: BoardNode): void {
    const rect = this.#viewport.getBoundingClientRect();
    const scale = this.#view.scale;
    const from = { ...this.#view };
    const to = { x: rect.width / 2 - (node.x + node.width / 2) * scale, y: rect.height / 2 - (node.y + node.height / 2) * scale };
    this.#selected = node.id;
    this.#render();
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / 300);
      const ease = 1 - (1 - t) ** 3;
      this.#view = { scale, x: from.x + (to.x - from.x) * ease, y: from.y + (to.y - from.y) * ease };
      this.#apply();
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  #toWorld(clientX: number, clientY: number): [number, number] {
    const rect = this.#viewport.getBoundingClientRect();
    return [(clientX - rect.left - this.#view.x) / this.#view.scale, (clientY - rect.top - this.#view.y) / this.#view.scale];
  }

  #centre(): [number, number] {
    const rect = this.#viewport.getBoundingClientRect();
    const [x, y] = this.#toWorld(rect.left + rect.width / 2 - 110, rect.top + rect.height / 2 - 60);
    return [Math.round(x), Math.round(y)];
  }

  #toggleList(): void {
    this.#list.hidden = !this.#list.hidden;
    this.#viewport.parentElement!.hidden = !this.#list.hidden;
    if (!this.#list.hidden) this.#renderList();
    else this.#fit();
  }

  #toggleFull(): void {
    this.classList.toggle("is-full");
    requestAnimationFrame(() => this.#fit());
  }

  #toggleRelate(): void {
    this.#relating = !this.#relating;
    this.#relateFrom = null;
    this.#pendingLabel = null;
    this.#relateButton.setAttribute("aria-pressed", String(this.#relating));
    this.#say(this.#relating ? "Click the node the relation starts from (Esc to cancel)." : "");
    this.#render();
  }

  // ---- Interaction ----

  #wireCanvas(): void {
    let drag: { kind: "pan" | "move" | "resize"; id?: string; startX: number; startY: number; ox: number; oy: number; moved: boolean } | null = null;

    this.#viewport.addEventListener("pointerdown", (event) => {
      if (event.button !== 0) return;
      const target = event.target as HTMLElement;
      if (target.closest("a, button, input, textarea, .board-relation-label")) return;
      const nodeEl = target.closest<HTMLElement>(".board-node");
      const node = nodeEl ? this.#node(nodeEl.dataset.id!) : undefined;
      if (node && this.#editing) {
        drag = target.closest(".board-resize")
          ? { kind: "resize", id: node.id, startX: event.clientX, startY: event.clientY, ox: node.width, oy: node.height, moved: false }
          : { kind: "move", id: node.id, startX: event.clientX, startY: event.clientY, ox: node.x, oy: node.y, moved: false };
      } else if (node) {
        // In view mode a node can't be moved; pressing it is a click (to read it), dragging it pans.
        drag = { kind: "pan", id: node.id, startX: event.clientX, startY: event.clientY, ox: this.#view.x, oy: this.#view.y, moved: false };
      } else {
        drag = { kind: "pan", startX: event.clientX, startY: event.clientY, ox: this.#view.x, oy: this.#view.y, moved: false };
      }
      this.#viewport.setPointerCapture(event.pointerId);
    });

    this.#viewport.addEventListener("pointermove", (event) => {
      if (!drag) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      if (!drag.moved && Math.abs(dx) + Math.abs(dy) < 4) return;
      drag.moved = true;
      if (drag.kind === "pan") {
        this.#view.x = drag.ox + dx;
        this.#view.y = drag.oy + dy;
        return this.#apply();
      }
      const node = this.#node(drag.id!);
      if (!node) return;
      const el = this.#world.querySelector<HTMLElement>(`.board-node[data-id="${node.id}"]`)!;
      if (drag.kind === "move") {
        node.x = Math.round(drag.ox + dx / this.#view.scale);
        node.y = Math.round(drag.oy + dy / this.#view.scale);
        Object.assign(el.style, { left: `${node.x}px`, top: `${node.y}px` });
      } else {
        node.width = Math.max(60, Math.round(drag.ox + dx / this.#view.scale));
        node.height = Math.max(40, Math.round(drag.oy + dy / this.#view.scale));
        Object.assign(el.style, { width: `${node.width}px`, height: `${node.height}px` });
      }
      this.#drawLines();
      this.#placePanel();
    });

    this.#viewport.addEventListener("pointerup", () => {
      const d = drag;
      drag = null;
      if (!d) return;
      const node = d.id ? this.#node(d.id) : undefined;
      if (!d.moved) {
        if (node) this.#click(node);
        else if (!this.#relating) {
          this.#closePanel();
          if (this.#selected) {
            this.#selected = null;
            this.#render();
          }
        }
        return;
      }
      if (node && this.#editing && d.kind !== "pan") {
        const patch = d.kind === "move" ? { x: node.x, y: node.y } : { width: node.width, height: node.height };
        void this.#call<BoardNode>("UpdateNode", { node: node.id }, patch);
      }
    });

    this.#viewport.addEventListener("click", (event) => {
      const labelEl = (event.target as HTMLElement).closest<HTMLElement>(".board-relation-label");
      if (labelEl && this.#editing) void this.#editRelation(labelEl.dataset.relations!.split(" "));
    });

    this.#viewport.addEventListener(
      "wheel",
      (event) => {
        // Plain scrolling scrolls the page; Ctrl + scroll (or pinch) zooms, and in full screen scrolling zooms too.
        if (!event.ctrlKey && !this.classList.contains("is-full")) return;
        event.preventDefault();
        const rect = this.#viewport.getBoundingClientRect();
        this.#zoomBy(event.deltaY < 0 ? 1.1 : 1 / 1.1, event.clientX - rect.left, event.clientY - rect.top);
      },
      { passive: false },
    );

    this.addEventListener("keydown", (event) => {
      const nodeEl = (event.target as HTMLElement).closest<HTMLElement>(".board-node");
      if (event.key === "Enter" && nodeEl) {
        const node = this.#node(nodeEl.dataset.id!);
        if (node) this.#click(node);
      }
      if (event.key === "Escape") {
        if (this.#relating) this.#toggleRelate();
        else if (!this.#panel.hidden) this.#closePanel();
        else if (this.classList.contains("is-full")) this.#toggleFull();
      }
    });
  }

  #click(node: BoardNode): void {
    if (this.#editing && this.#relating) {
      if (this.#relateFrom === null) {
        this.#relateFrom = node.id;
        this.#say("Now click the node it points to.");
        return this.#render();
      }
      if (this.#relateFrom !== node.id) void this.#addRelation(this.#relateFrom, node.id);
      this.#relating = false;
      this.#relateFrom = null;
      this.#relateButton.setAttribute("aria-pressed", "false");
      return this.#render();
    }
    if (this.#editing) this.#openEditor(node);
    else this.#centreOn(node);
  }

  async #addNote(): Promise<void> {
    const [x, y] = this.#centre();
    const node = await this.#call<BoardNode>("AddNode", {}, { kind: "note", title: "New note", x, y, shape: "note" });
    if (node) {
      this.#replace(node);
      this.#openEditor(node);
    }
  }

  async #addRelation(from: string, to: string): Promise<void> {
    const pending = this.#pendingLabel;
    this.#pendingLabel = null;
    const labelText = pending !== null ? pending : (window.prompt("Label for the relation (optional), e.g. “sire of”:", "") ?? null);
    if (labelText === null) return;
    const relation = await this.#call<Relation>("AddRelation", {}, { from, to, label: labelText });
    if (relation && this.#board) {
      this.#board.relations.push(relation);
      this.#render();
    }
  }

  // Changes or removes a relation, or both halves of a two-way one at once.
  async #editRelation(ids: string[]): Promise<void> {
    const relations = (this.#board?.relations ?? []).filter((r) => ids.includes(r.id));
    const relation = relations[0];
    if (!relation) return;
    const arrow = relations.length > 1 ? "↔" : "→";
    const answer = window.prompt(
      `Relation ${this.#name(relation.from)} ${arrow} ${this.#name(relation.to)}.\nChange the label, or write “remove” to delete the relation:`,
      relation.label ?? "",
    );
    if (answer === null) return;
    const remove = answer.trim().toLowerCase() === "remove";
    for (const r of relations) {
      if (remove) {
        if (await this.#call("DeleteRelation", { relation: r.id }, {})) this.#board!.relations = this.#board!.relations.filter((x) => x.id !== r.id);
      } else if (await this.#call("UpdateRelation", { relation: r.id }, { label: answer })) {
        r.label = answer.trim() || null;
      }
    }
    this.#render();
  }

  async #makeTie(r: Relation): Promise<void> {
    const kind = window.prompt(
      `Make a tie: ${this.#name(r.from)} → ${this.#name(r.to)}.\nKind of tie, e.g. “owes a debt to”, or its reverse, e.g. “is owed a debt by” (“not set” for no kind yet):`,
      r.label ?? "",
    );
    if (kind === null) return;
    if (await this.#call("MakeTie", { relation: r.id }, { label: kind })) this.#say("Tie made. It's on the page's Ties box; this board stays as it is.");
  }

  async #deleteBoard(): Promise<void> {
    if (!window.confirm("Delete this board, its nodes and relations for good? It's no longer in the page's text.")) return;
    if (await this.#call("DeleteBoard", {}, {})) this.closest("[data-board-entry]")?.remove() ?? this.remove();
  }

  // ---- The floating panel: details (view mode) or editor (edit mode) ----

  // Next to its node: to the right if there's room, else to the left, else over the canvas; always inside it.
  #placePanel(): void {
    if (this.#panel.hidden) return;
    const stage = this.#panel.parentElement!.getBoundingClientRect();
    if (stage.width === 0) return;
    const width = Math.min(352, stage.width - 16);
    this.#panel.style.width = `${width}px`;
    const id = this.#panel.dataset.node;
    const el = id ? this.#world.querySelector<HTMLElement>(`.board-node[data-id="${id}"]`) : null;
    let left = stage.width - width - 8;
    let top = 8;
    if (el) {
      const r = el.getBoundingClientRect();
      const right = r.right - stage.left + 12;
      const leftSide = r.left - stage.left - width - 12;
      left = right + width <= stage.width - 8 ? right : leftSide >= 8 ? leftSide : Math.max(8, stage.width - width - 8);
      top = Math.min(Math.max(8, r.top - stage.top), Math.max(8, stage.height - this.#panel.offsetHeight - 8));
    }
    Object.assign(this.#panel.style, { left: `${left}px`, top: `${top}px` });
  }

  #showPanel(node: BoardNode | null, heading: string): void {
    const form = this.#panel;
    form.replaceChildren();
    form.hidden = false;
    form.dataset.node = node?.id ?? "";
    this.#selected = node?.id ?? null;
    const head = div("board-panel-head");
    const h = document.createElement("h3");
    h.textContent = heading;
    const close = button("×", "Close", () => this.#closePanel());
    close.className = "link board-panel-close";
    head.append(h, close);
    form.append(head);
    this.#render();
  }

  #closePanel(): void {
    if (this.#panel.hidden) return;
    this.#panel.hidden = true;
    this.#panel.dataset.node = "";
    this.#selected = null;
    this.#render();
  }

  // Edit mode: the node editor, starting with the buttons that grow the web from here.
  #openEditor(node: BoardNode | null, kind: "note" | "page" = "note"): void {
    const board = this.#board!;
    const isPage = node ? node.kind === "page" : kind === "page";
    this.#showPanel(node, node ? "Node" : isPage ? "Add a page node" : "Add a note");
    const form = this.#panel;
    const tokenInput = document.createElement("input");
    tokenInput.type = "hidden";
    tokenInput.name = "__RequestVerificationToken";
    tokenInput.value = token();
    form.append(tokenInput);

    if (node) {
      const quick = div("board-quick");
      const relLabel = textInput("relationLabel", "", "Relation, e.g. sire of");
      relLabel.setAttribute("aria-label", "Label for the new relation");
      quick.append(
        relLabel,
        button("+ Related node", "Add a new node next to this one, connected to it", () => void this.#addRelated(node, relLabel.value)),
        button("Connect to…", "Then click the node this one points to", () => this.#startRelate(node, relLabel.value)),
      );
      form.append(quick);
    }

    const fields = div("stack");
    let pageField: HTMLInputElement | null = null;
    if (isPage) {
      pageField = document.createElement("input");
      pageField.type = "hidden";
      pageField.name = "pageId";
      pageField.value = node?.pageId ?? "";
      const picker = document.createElement("p9-autocomplete");
      picker.dataset.url = `${this.#base}lookup`;
      picker.dataset.kinds = "pages";
      picker.dataset.pickPage = "pageId";
      const input = document.createElement("input");
      input.type = "text";
      input.placeholder = node?.page?.title ?? "Find a page…";
      input.autocomplete = "off";
      input.setAttribute("aria-label", "Page");
      picker.append(input);
      fields.append(labelled("Page", picker), pageField);
    }
    const title = textInput("title", node?.title ?? "", isPage ? "Own heading (optional; the page title otherwise)" : "Heading");
    const body = document.createElement("textarea");
    body.name = "body";
    body.rows = 5;
    body.value = node?.body ?? "";
    body.placeholder = "Text: Markdown, [[ page links, images";
    fields.append(labelled("Heading", title), labelled("Text", body));

    const style = div("board-style");
    style.append(
      choices("Shape", "shape", board.shapes, node?.shape ?? (isPage ? "card" : "note"), (v) => SHAPE_LABELS[v] ?? v),
      choices("Colour", "color", board.colors, node?.color ?? "default", (v) => v),
      choices("Border", "border", board.borders, node?.border ?? "solid", (v) => v),
    );
    fields.append(style);

    const restricted = textInput("restrictedTo", node?.restrictedTo ?? "", "@viola #gms (empty: everyone who sees the page)");
    const noAi = document.createElement("input");
    noAi.type = "checkbox";
    noAi.name = "noAi";
    noAi.checked = node?.noAi ?? false;
    const noAiLabel = document.createElement("label");
    noAiLabel.className = "check";
    noAiLabel.append(noAi, " Never show to AI");
    fields.append(labelled("Visible to", restricted), noAiLabel);
    fields.append(labelled("Tags", this.#tagField(node?.tags ?? [])));

    if (node) {
      const relations = board.relations.filter((r) => r.from === node.id || r.to === node.id);
      if (relations.length) {
        const list = document.createElement("ul");
        list.className = "board-relations";
        for (const r of relations) {
          const li = document.createElement("li");
          li.textContent = r.from === node.id ? `${r.label ?? ""} → ${this.#name(r.to)}` : `${this.#name(r.from)} → ${r.label ?? ""} (to this)`;
          li.append(" ", button("Change", "Change or remove", () => void this.#editRelation([r.id])));
          // Between two pages it can become a tie (docs/tapestry-design.md §3.9); the board stays as it is.
          if (this.#node(r.from)?.page && this.#node(r.to)?.page)
            li.append(" ", button("Make it a tie", "Record how these two pages stand as a tie (on the first page, or on the second for a kind's reverse)", () => void this.#makeTie(r)));
          list.append(li);
        }
        fields.append(labelled("Relations", list));
      }
    }
    form.append(fields);

    const save = async (): Promise<void> => {
      const data = new FormData(form);
      const payload = {
        kind: isPage ? "page" : "note",
        title: String(data.get("title") ?? ""),
        body: String(data.get("body") ?? ""),
        pageId: pageField?.value || null,
        shape: String(data.get("shape") ?? "card"),
        color: String(data.get("color") ?? "default"),
        border: String(data.get("border") ?? "solid"),
        restrictedTo: String(data.get("restrictedTo") ?? ""),
        noAi: noAi.checked,
        tagIds: data.getAll("TagIds").map(String),
      };
      if (isPage && !payload.pageId) return this.#say("Pick a page from the list first.");
      const [x, y] = this.#centre();
      const result = node
        ? await this.#call<BoardNode>("UpdateNode", { node: node.id }, payload)
        : await this.#call<BoardNode>("AddNode", {}, { ...payload, x, y });
      if (!result) return;
      this.#closePanel();
      this.#replace(result);
      if (result.gone) this.#say("Saved. You can't see it any more, given who it's visible to.");
    };

    const actions = div("form-actions");
    const saveButton = button(node ? "Save" : "Add", "", () => void save());
    saveButton.className = "button";
    actions.append(saveButton, button("Close", "", () => this.#closePanel()));
    if (node) {
      const del = button("Delete node", "Delete this node and its relations", () => void this.#deleteNode(node));
      del.className = "link danger";
      actions.append(del);
    }
    form.append(actions);
    form.onsubmit = (event) => {
      event.preventDefault();
      void save();
    };
    requestAnimationFrame(() => {
      this.#placePanel();
      title.focus({ preventScroll: true });
    });
  }

  #tagField(current: Tag[]): HTMLElement {
    const tags = document.createElement("p9-tags");
    tags.dataset.suggestUrl = `${this.#base}tags?handler=Suggest`;
    tags.dataset.checkUrl = `${this.#base}tags?handler=Check`;
    tags.dataset.createUrl = `${this.#base}tags?handler=Create`;
    const chipList = document.createElement("ul");
    chipList.className = "tag-chips";
    chipList.dataset.chips = "";
    for (const t of current) {
      const li = document.createElement("li");
      li.className = "tag-chip";
      const span = document.createElement("span");
      span.textContent = t.label;
      const remove = button("×", `Remove ${t.label}`, () => li.remove());
      remove.className = "link";
      remove.dataset.remove = "";
      const hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = "TagIds";
      hidden.value = t.id;
      li.append(span, remove, hidden);
      chipList.append(li);
    }
    const tagInput = document.createElement("input");
    tagInput.type = "text";
    tagInput.dataset.tagInput = "";
    tagInput.placeholder = "Add a tag, e.g. Type: NPC";
    tagInput.autocomplete = "off";
    const menu = div("tag-menu");
    menu.dataset.menu = "";
    menu.hidden = true;
    tags.append(chipList, tagInput, menu);
    return tags;
  }

  // A new note to the right of this one (moving down past anything in the way), related to it, then edited.
  async #addRelated(from: BoardNode, relationLabel: string): Promise<void> {
    const nodes = this.#board?.nodes ?? [];
    const x = from.x + from.width + 90;
    let y = from.y;
    const overlaps = (yy: number) => nodes.some((n) => x < n.x + n.width + 20 && x + 240 > n.x && yy < n.y + n.height + 20 && yy + 140 > n.y);
    while (overlaps(y)) y += 140;
    const node = await this.#call<BoardNode>("AddNode", {}, { kind: "note", title: "New note", x: Math.round(x), y: Math.round(y), shape: "note" });
    if (!node) return;
    this.#closePanel();
    this.#replace(node);
    const relation = await this.#call<Relation>("AddRelation", {}, { from: from.id, to: node.id, label: relationLabel });
    if (relation && this.#board) this.#board.relations.push(relation);
    this.#render();
    this.#openEditor(node);
  }

  #startRelate(from: BoardNode, relationLabel: string): void {
    this.#closePanel();
    this.#relating = true;
    this.#relateFrom = from.id;
    this.#pendingLabel = relationLabel;
    this.#relateButton.setAttribute("aria-pressed", "true");
    this.#say("Click the node it points to (Esc to cancel).");
    this.#render();
  }

  async #deleteNode(node: BoardNode): Promise<void> {
    if (!window.confirm(`Delete “${node.title || node.page?.title || "this node"}” and its relations?`)) return;
    if (await this.#call("DeleteNode", { node: node.id }, {})) {
      this.#closePanel();
      this.#replace({ ...node, gone: true });
    }
  }

  #say(message: string): void {
    this.#status.textContent = message;
  }
}

// ---- Helpers ----

function sameLabel(a: string | null, b: string | null): boolean {
  return (a ?? "").trim().toLowerCase() === (b ?? "").trim().toLowerCase();
}

function token(): string {
  return (
    document.querySelector<HTMLMetaElement>('meta[name="request-token"]')?.content ??
    document.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]')?.value ??
    ""
  );
}

function div(className: string): HTMLDivElement {
  const el = document.createElement("div");
  el.className = className;
  return el;
}

function button(text: string, title: string, run: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.textContent = text;
  if (title) b.title = title;
  b.addEventListener("click", run);
  return b;
}

function label(text: string, className: string): HTMLElement {
  const el = document.createElement("span");
  el.className = className;
  el.textContent = text;
  return el;
}

function chips(tags: Tag[], className: string): HTMLElement {
  const ul = document.createElement("ul");
  ul.className = `tag-chips ${className}`;
  for (const t of tags) {
    const li = document.createElement("li");
    li.className = "tag-chip";
    li.textContent = t.label;
    ul.append(li);
  }
  return ul;
}

function textInput(name: string, value: string, placeholder: string): HTMLInputElement {
  const input = document.createElement("input");
  input.type = "text";
  input.name = name;
  input.value = value;
  input.placeholder = placeholder;
  return input;
}

function labelled(text: string, control: HTMLElement): HTMLElement {
  const wrap = document.createElement("label");
  wrap.className = "board-field";
  const caption = document.createElement("span");
  caption.textContent = text;
  wrap.append(caption, control);
  return wrap;
}

// A row of buttons that sets a hidden field (menus instead of <select>, as the owner prefers).
function choices(caption: string, name: string, values: string[], current: string, text: (v: string) => string): HTMLElement {
  const wrap = document.createElement("fieldset");
  wrap.className = `board-choices board-choices-${name}`;
  const legend = document.createElement("legend");
  legend.textContent = caption;
  const hidden = document.createElement("input");
  hidden.type = "hidden";
  hidden.name = name;
  hidden.value = current;
  wrap.append(legend, hidden);
  for (const value of values) {
    const b = button(text(value), text(value), () => {
      hidden.value = value;
      for (const other of wrap.querySelectorAll("button")) other.setAttribute("aria-pressed", String(other === b));
    });
    b.dataset.value = value;
    b.className = name === "color" ? `swatch color-${value}` : "";
    b.setAttribute("aria-pressed", String(value === current));
    wrap.append(b);
  }
  return wrap;
}

// Where the line from a's centre towards the point (px, py) leaves a's box.
function edgeToward(a: BoardNode, px: number, py: number): [number, number] {
  const ax = a.x + a.width / 2;
  const ay = a.y + a.height / 2;
  const dx = px - ax;
  const dy = py - ay;
  if (dx === 0 && dy === 0) return [ax, ay];
  const sx = Math.abs(dx) > 0 ? a.width / 2 / Math.abs(dx) : Infinity;
  const sy = Math.abs(dy) > 0 ? a.height / 2 / Math.abs(dy) : Infinity;
  const s = Math.min(sx, sy);
  return [ax + dx * s, ay + dy * s];
}

// Shapes drawn as SVG behind the text: the path stretches with the node, the outline keeps its width.
function shapeSvg(shape: string): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 100 100");
  svg.setAttribute("preserveAspectRatio", "none");
  svg.classList.add("board-shape");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("d", shapePath(shape));
  path.setAttribute("vector-effect", "non-scaling-stroke");
  svg.append(path);
  if (shape === "cloud") {
    for (const [cx, cy, r] of [
      [14, 92, 4],
      [6, 99, 2.5],
    ] as const) {
      const c = document.createElementNS(SVG_NS, "ellipse");
      c.setAttribute("cx", String(cx));
      c.setAttribute("cy", String(cy));
      c.setAttribute("rx", String(r));
      c.setAttribute("ry", String(r));
      c.setAttribute("vector-effect", "non-scaling-stroke");
      svg.append(c);
    }
  }
  return svg;
}

export function shapePath(shape: string): string {
  if (shape === "shout") {
    // A comic "shout" burst: spikes alternating out and in around the centre.
    const points: string[] = [];
    const spikes = 14;
    for (let i = 0; i < spikes * 2; i++) {
      const angle = (Math.PI * i) / spikes - Math.PI / 2;
      const r = i % 2 === 0 ? 50 : 36 + ((i * 7) % 5);
      points.push(`${(50 + r * Math.cos(angle)).toFixed(1)},${(50 + r * Math.sin(angle)).toFixed(1)}`);
    }
    return `M${points.join(" L")} Z`;
  }
  if (shape === "cloud") {
    // Bumps along an ellipse (leaving room at the bottom left for the thought bubbles).
    const bumps = 10;
    let d = "";
    for (let i = 0; i <= bumps; i++) {
      const angle = (2 * Math.PI * i) / bumps;
      const x = 50 + 44 * Math.cos(angle);
      const y = 44 + 36 * Math.sin(angle);
      if (i === 0) d += `M${x.toFixed(1)},${y.toFixed(1)}`;
      else {
        const mid = (2 * Math.PI * (i - 0.5)) / bumps;
        d += ` Q${(50 + 56 * Math.cos(mid)).toFixed(1)},${(44 + 48 * Math.sin(mid)).toFixed(1)} ${x.toFixed(1)},${y.toFixed(1)}`;
      }
    }
    return d + " Z";
  }
  // Speech bubble: a rounded box with a tail at the bottom left.
  return "M8,2 H92 Q98,2 98,8 V72 Q98,78 92,78 H34 L14,98 L20,78 H8 Q2,78 2,72 V8 Q2,2 8,2 Z";
}

customElements.define("p9-board", P9Board);
